import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import PropTypes from 'prop-types';
import { Col, Checkbox, Row, Drawer, Button, Icon } from "antd";
import _, { set } from 'lodash';
import Pagination from "../common/Pagination";
import GridPagination from '../common/GridPagination';
import Checkbox_v2 from '../../stories/components/Checkbox_v2';
import { alertMessage } from "../../common/Common";
import SettingIcon from '../common/SettingIcon';
import { ensureCompanyColumns, normalizeCompanyColumns } from '../../helpers/common';

const pageSizeOptions = [
  // Smaller sizes so a screen's saved/default page size (e.g. 20 or 40) is a
  // real option and antd renders it as "N / page" instead of a bare number.
  { value: 20, label: "20 per page" },
  { value: 40, label: "40 per page" },
  { value: 50, label: "50 per page" },
  { value: 100, label: "100 per page" },
  { value: 200, label: "200 per page" },
  { value: 400, label: "400 per page" },
  { value: 600, label: "600 per page" },
  { value: 800, label: "800 per page" },
  { value: 1000, label: "1000 per page" },
  { value: 1200, label: "1200 per page" },
];

const MyGridComponent = (props) => {
  const {
    columnDefs,
    totalCols,
    rowData,
    additionalColumnDefs = {},
    onRowSelection,
    saveColumnWidth,
    onColumnMoved,
    onRowMoved,
    onSortChanged,
    showRerraangeModal,
    closeModal,
    handleColumnVisibilityChange,
    handleRowSelection,
    showPagination= false,
    pagination = {},
    enableRowClick,
    showQuickJumper,
    handleCellData = () => {},
    selectedIds = [],
    shouldResetRowSelectedKeys,
    restoreRowSelection,
    addDropZones = () => {},
    onGridReady = () => {},
    filterPlaceHolder = {},
    RowHeight = 40,
    loading = false,
    showFilter = true,
    uniqCols = [],
    components = {},
    showColumnToggle = true,
    enableRowDragEntireRow = false,
  } = props;
  const gridApiRef = useRef(null);
  const applyingSelectedIdsRef = useRef(false);
  const [visibleCols, setVisibleCols] = useState([]);
  const resizedColsRef = useRef(new Map()); // Track manually resized columns with their widths
  const defaultColDef = React.useMemo(() => {
    return {
      sortable: true,
      filter: showFilter ? true : false,
      resizable: true,
      lockPinned : true,
      suppressMenu: true,
      floatingFilter: showFilter ? true : false,
      ...additionalColumnDefs
    };
  }, [ additionalColumnDefs ]);

  const updateVisibleCols = (newCols) => {
    setVisibleCols(newCols);
  };

  const handleColumnResized = useCallback((event) => {
    // Only react to a genuine user drag of the resize handle. Every other
    // source is programmatic (api / flex / sizeColumnsToFit / autosizeColumns
    // and "gridOptionsChanged" on first load) and must not flash the
    // "too small" warning or persist a width — that was the first-load flash.
    if (event.source !== "uiColumnResized") return;
    const column = event.column;
    if(!column) return;
    // Non-resizable columns can't be user-resized, but guard anyway.
    if (column.getDefinition && column.getDefinition().resizable === false) return;
    const minWidth = column.getDefinition().minWidth || 80;
    const actualWidth = column.actualWidth;

    if (actualWidth <= minWidth) {
      alertMessage(`Column cannot be rezised as it is too small`, "warning");
    }

    if (event.finished) {
      const colId = column.colId;
      const colState = gridApiRef.current.getColumnState();
      const newWidth = Math.max(actualWidth, minWidth);

      // Store the resized width
      resizedColsRef.current.set(colId, newWidth);

      // Update column definition to remove flex and set fixed width
      const updatedCols = visibleCols.map(col => {
        if (col.field === colId) {
          return { ...col, width: newWidth, flex: undefined };
        }
        return col;
      });
      setVisibleCols(updatedCols);

      saveColumnWidth({colId, newWidth, colState, columnDefs});
    }
  }, [columnDefs, saveColumnWidth, visibleCols]);


  const selectAllColumns = () => {
    const allColumns = _.get(props, 'totalCols', []).filter(obj => obj.headerName || obj.headerName === '' || !_.has(obj, "headerName"));
    setVisibleCols(allColumns);
  };

  const clearTextSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      selection.removeAllRanges();
    }
  };

  const handleColumnMoved = useCallback((event) => {
    if(event.finished) {
      const column = event.column;
      if(!column) return;
      const colId = column.colId;
      const colIndex = event.toIndex;
      const colState = gridApiRef.current.getColumnState();
      onColumnMoved(colState);
    }
  }, [onColumnMoved]);

  const onSelectionChanged = useCallback((event) => {
    if (props.source === "unassignedOrders" && applyingSelectedIdsRef.current) return;
    const selectedNodes = event.api.getSelectedNodes();
    // const selectedData = selectedNodes.map(node => node.data);
    // fetch ids and data in single call
    const selectedData = selectedNodes.map(node => {
      return {
        id: node.data.id,
        data: node.data
      };
    });
    const selectedIds = selectedData.map(node => node.id);
    const selectedOrderData = selectedData.map(node => node.data);

    if (props.source === "unassignedOrders") {
      onRowSelection(selectedIds, selectedOrderData, event);
    } else {
      onRowSelection(selectedIds, selectedOrderData);
    }
  }, [onRowSelection, props.source]);

  const onRowClicked = useCallback((event) => {
    const clickedRowId = event.data.id;
    // // Check if the clicked row is selected
    // const isSelected = gridApiRef.current.isSelectedNode(event.node);
    // onSelectionChanged(event)
  }, []);

  const clearSort = useCallback(() => {
    gridApiRef.current.applyColumnState({
      defaultState: { sort: null },
    });
  }, []);

  useEffect(() => {
    if (!_.isEmpty(filterPlaceHolder) && filterPlaceHolder?.sortBy !== "none" && !_.isEmpty(gridApiRef.current)) {
      gridApiRef.current.applyColumnState({
        state: [
          {
            colId: props.filterPlaceHolder.sortBy, 
            sort: props.filterPlaceHolder.sortByType == "descend" ? "desc" : "asc", 
          },
        ],
        defaultState: { sort: null },
      });
    }
  }, [filterPlaceHolder]);

  const saveSort = useCallback(() => {
    var colState = gridApiRef.current.getColumnState();
    var sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });
    // if sortState is empty then just reset sort to default
    if (sortState.length === 0) {
      clearSort();
    } else {
      onSortChanged(sortState);
    }
  }, [onSortChanged, clearSort]);

  useEffect(() => {
    // Preserve manually resized column widths when columnDefs change
    const updatedCols = columnDefs.map(col => {
      const resizedWidth = resizedColsRef.current.get(col.field);
      // Never let the resize cache override a non-resizable column (e.g. the
      // pinned actions column, whose width is computed dynamically per tab).
      // A non-resizable column can't be user-resized, so any cached width is
      // stale and would clobber its colDef width and clip content.
      if (resizedWidth && col.resizable !== false) {
        // Column was manually resized, use fixed width
        return { ...col, width: resizedWidth, flex: undefined };
      }
      // If column already has a fixed width (no flex), keep it as is
      // If column has flex, it will be preserved for auto-sizing
      return col;
    });
    setVisibleCols(updatedCols);
  }, [ columnDefs ]);

  // Force non-resizable columns (e.g. the pinned actions column) to their
  // colDef width. AG-Grid preserves a column's width across columnDefs
  // updates, so a per-tab dynamic width would otherwise stay stuck at the
  // first render's value and clip content when a later tab shows more icons.
  // Non-resizable columns are never user-resized, so overriding is safe.
  const applyFixedColumnWidths = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || !visibleCols?.length) return;
    const state = visibleCols
      .filter((c) => c && c.resizable === false && c.width)
      .map((c) => ({ colId: c.field || c.colId, width: c.width }));
    if (state.length && typeof api.applyColumnState === "function") {
      api.applyColumnState({ state });
    }
  }, [visibleCols]);

  useEffect(() => {
    applyFixedColumnWidths();
  }, [applyFixedColumnWidths]);

  useEffect(() => {
    // Check if the source is "unassignedOrders"
    if (props.source !== "unassignedOrders") {
      return;
    }
    // If there are no selected IDs, deselect all nodes and return
    const api = gridApiRef.current;
    const applySelection = (run) => {
      applyingSelectedIdsRef.current = true;
      try {
        run();
      } finally {
        applyingSelectedIdsRef.current = false;
      }
    };
    if(!selectedIds || selectedIds.length === 0 ) {
      if(api){
        applySelection(() => api.deselectAll());
      }
      return;
    };

    // If gridApiRef is not available, call onRowSelection with empty arrays and return
    if(!api){
      onRowSelection([],[]);
      return;
    }

    const nodesToSelect = [];

    // Iterate through each node and push nodes with selected IDs to nodesToSelect array
    api.forEachNode((node) => {
      if(node && node.data && selectedIds.includes(node.data.id)){
        nodesToSelect.push(node);
      }
    });

    applySelection(() => {
      api.deselectAll();
      if(nodesToSelect.length > 0){
        api.setNodesSelected({
          nodes: nodesToSelect,
          newValue : true,
        });
      }
    });

  }, [selectedIds]);

  useEffect(() => {
    if(shouldResetRowSelectedKeys){
      gridApiRef.current.deselectAll();
      restoreRowSelection();
    }
  }, [shouldResetRowSelectedKeys]); 

  useEffect(() => {
    if (gridApiRef.current) {
      if (loading) {
        gridApiRef.current.showLoadingOverlay();
      } else {
        gridApiRef.current.hideOverlay();
      }
    }
  }, [ loading ]);

  const handleResetColumnsOrder = () => {
    // Clear resized columns cache when resetting
    resizedColsRef.current.clear();

    const newCols = totalCols.map((obj) => {
      //find the column in the visibleCols
      const foundObj = visibleCols.find(col => col.field === obj.field)

      // replace the isvisible of found Column in totalCols
      return {
        ...obj,
        isVisible: foundObj ? foundObj.isVisible : false
      }
    })
    setVisibleCols(newCols);
    onRowMoved(newCols);
  }

  const handleColAllVisibility = () => {
    if (!visibleCols || !totalCols) return; // Prevents null/undefined errors
  
    const checkboxCol = totalCols.find((col) => col.field === "0");
    const regularCols = totalCols.filter(
      (obj) => obj.headerName !== undefined && obj.headerName !== ""
    );
    const alwaysVisibleCols = totalCols.filter(
      (obj) => obj.headerName === undefined || obj.headerName === ""
    );

    const visibleIndex = visibleCols.reduce((acc, col, index) => {
      acc[col.field] = index;
      return acc;
    }, {});

    const orderedRegularCols = [...regularCols].sort((a, b) => {
      const aIndex = a.field in visibleIndex ? visibleIndex[a.field] : Number.MAX_SAFE_INTEGER;
      const bIndex = b.field in visibleIndex ? visibleIndex[b.field] : Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });

    const allCheckedRegularCols = orderedRegularCols.map((col) => ({
      ...col,
      isVisible: true,
    }));

    const otherAlwaysVisibleCols = alwaysVisibleCols
      .filter((col) => col.field !== "0")
      .map((col) => ({ ...col, isVisible: true }));

    const updatedCols = checkboxCol
      ? [{ ...checkboxCol, isVisible: true }, ...allCheckedRegularCols, ...otherAlwaysVisibleCols]
      : [...allCheckedRegularCols, ...otherAlwaysVisibleCols];

    setVisibleCols(updatedCols);
    onRowMoved(updatedCols);
  }

  const handleColVisibility = useCallback(
    (currentColObj) => {
      const col = gridApiRef.current.getColumnState();
       let configValues = normalizeCompanyColumns(col);
      const colState = ensureCompanyColumns(configValues);
      const foundCol = colState.find(
        (col) => col.colId === currentColObj.field
      );

      if (foundCol) {
        let newCols;

        if (currentColObj.isVisible) {
          // If the column is currently visible, update isVisible to false
          newCols = visibleCols.map((item) =>
            item.field === currentColObj.field
              ? { ...item, isVisible: false }
              : item
          );
        } else {
          // If the column is currently hidden, update isVisible to true
          newCols = visibleCols.map((item) =>
            item.field === currentColObj.field
              ? { ...item, isVisible: true }
              : item
          );
        }
        setVisibleCols(
          newCols.filter(
            (col) =>
              col.isVisible ||
              col.headerName === undefined ||
              col.headerName === ""
          )
        );
        const colState = gridApiRef.current.getColumnState();
        onRowMoved(newCols);
      } else {
        const actualColObj = totalCols.find(
          (col) => col.field === currentColObj.field
        );

        const newCols = [
          ...visibleCols,
          {
            ...actualColObj,
            isVisible: true,
          },
        ];
        setVisibleCols(newCols);

        const colState = gridApiRef.current.getColumnState();
        onRowMoved(newCols);
      }
    },
    [totalCols, visibleCols, setVisibleCols, gridApiRef, onRowMoved]
  );
  
const renderColumns = useCallback(() => {
  const filteredCols = totalCols.filter(obj => obj.headerName !== undefined && obj.headerName !== "" && !obj.lockVisible);


  // Create an object that maps the field of each visible column to its index
  const visibleIndex = visibleCols.reduce((acc, col, index) => {
    acc[ col?.field ] = index;
    return acc;
  }, {});

  const testRowData = filteredCols.map((obj , index) => {
    const visibleCol = visibleCols?.find((col) => col?.field === obj?.field);
    return {
      headerName: obj?.headerName,
      field: obj?.field,
      colId: obj?.field,
      isVisible: visibleCol
        ? visibleCol.isVisible !== false
        : false,
      index: obj?.field in visibleIndex ? visibleIndex[obj?.field] : index,
    };
  });

  // Sort testRowData by the index property to maintain the same order as visibleCols
  testRowData.sort((a, b) => a.index - b.index);

  const visibleColumns = testRowData?.filter(col => col.isVisible);
  const invisibleColumns = testRowData?.filter(col => !col.isVisible);
  const updatedTestRowData = [...visibleColumns, ...invisibleColumns];
  const columns = [
    {
      headerName: "Columns Settings",
      field: "headerName",
      width: "285px",
      suppressMovable: true,
      rowDrag: (params) => {
        const isDisabled = uniqCols.includes(params.data.field)
        return params.data.isVisible && !isDisabled; 
      },

      // rowDrag: (params) => params.data.isVisible, // Conditionally enable row drag based on visibility
      headerComponent: () => (
        <Row style={{ display: "flex", justifyContent:"space-between" }}>
          <Col xs={17} style={{fontSize:"20px", position:"relative", color:"#273156"}}>
            Columns Settings
          </Col>
          <Col xs={7} size='small' type='primary'  onClick={handleColAllVisibility}
          style={{fontSize:"14px", position:"relative", left:"40px", top:"4px", color:"#586FCC", fontWeight:"600", cursor:"pointer"}}
          >
            Check All
          </Col>
        </Row>
      ),
      cellRenderer: (obj) => {
        const isCompanyPickup = obj.data.field === "company_name_pickup";
        const isCompanyDelivery = obj.data.field === "company_name_delivery";

        // disable delivery row completely
        const isDisabled =
          uniqCols.includes(obj.data.field) || isCompanyDelivery;

        const headerName = obj.data.headerName;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "14px",
              opacity: isDisabled ? 0.5 : 1,
              cursor: isDisabled ? "not-allowed" : "default"
            }}
          >
            <div>{headerName}</div>

            <Checkbox_v2
              dataTestId={`${headerName?.toLowerCase().replace(/\s+/g, '-')}-checkbox`}
              checkValue={obj.data.isVisible}
              handleChange={() => !isDisabled && handleColVisibility(obj?.data)}
              style={{ paddingRight: "5px" }}
              size="small"
              disabled={isDisabled}
            />
          </div>
        );
      }
    },
  ];
  
  return (
      <div data-testid="columns-settings-drawer" style={{ height: "96vh", width:"100%" }} className={`ToggleColumnsStyles${props.source === "unassignedOrders" ? " preplan-unassigned-grid" : ""}`}>
        <AgGridReact
            columnDefs={columns}
            rowData={updatedTestRowData}
            rowDragManaged={true}
            //rowDragManaged={false}
            onRowDragEnd={handleRowDragEnd}
            isVisible={false}
            onColumnVisible={handleColVisibility}
            onColumnMoved={ handleColumnMoved }
            rowHeight={45}
            headerHeight={60}
            onRowSelected={handleRowSelection}
            suppressMovableColumns={false}
            className="ag-theme-alpine"
          />
      </div>
  );
}, [visibleCols , columnDefs]);

const handleRowDragEnd = useCallback(
  (event) => {
    const {
      overIndex,
      node: { data },
    } = event;

    // Filter out the column with field "0"
    const checkboxCol = visibleCols.find((col) => col.field === "0");
    const newCols = visibleCols.filter((col) => col.field !== "0");

    const draggedColumnIndex = newCols.findIndex(
      (col) => col.field === data.field
    );

    if (draggedColumnIndex !== -1 && draggedColumnIndex !== overIndex) {
      const draggedColumn = newCols.splice(draggedColumnIndex, 1)[0];
      newCols.splice(overIndex, 0, draggedColumn);

      // Re-add the checkbox column to the new columns list
      if (checkboxCol) {
        newCols.unshift(checkboxCol);
      }

      updateVisibleCols(newCols);
      onColumnMoved(newCols);
    }
  },
  [visibleCols, updateVisibleCols, onColumnMoved]
);
  
  
  const isRowSelectable = useCallback((rowNode) => {
    return props.isRowSelectable ? props.isRowSelectable(rowNode) : true;
  }, []);

  const onRowDataUpdated = useCallback((params) => {
    if(props.onRowDataUpdated){
      props.onRowDataUpdated(params)
    }
  }, [props.onRowDataUpdated]);

  const handleGetRowStyle =  useCallback((params) => {
     const { getRowStyle } = props;
    if (getRowStyle) {
      return getRowStyle(params); // Call the provided getRowStyle function if it exists
    }
  }, [props.getRowStyle]);

  const tabToNextCell = useCallback(
    function(params) {
      const previousCell = params.previousCellPosition;
      const renderedRowCount = params.api.getDisplayedRowCount();
      const lastRowIndex = previousCell.rowIndex;
      let nextRowIndex = params.backwards ? lastRowIndex - 1 : lastRowIndex + 1;
      if (nextRowIndex < 0) {
        nextRowIndex = -1;
      }
      if (nextRowIndex >= renderedRowCount) {
        nextRowIndex = renderedRowCount - 1;
      }
      const result = {
        rowIndex: nextRowIndex,
        column: previousCell.column,
        rowPinned: previousCell.rowPinned,
      };
      return result;
    },
    []
  );

  
  const LoadingOverlay = () => {
    return (
      <div className="ag-overlay-loading-center" style={ { backgroundColor: 'rgba(255, 255, 255, 0.8)' } }>
        <div className="ag-overlay-loading-center-inner">
          <div className="spinner"></div>
          <span className="loading-text">Loading...</span>
        </div>
      </div>
    );
  }

  const rowClassRules = {
    "ag-row-highlighted": (params) => {
      if (params.data.highlighted) return true;
      if (props.source === "quotes") {
        return (params.data.source === "SHIPPER_PORTAL" || params.data.source === "SHIPPER_PORTAL_EXTERNAL");
      }
      return false;
    },
    "ag-row-blockBgErrorColor": (params) => {
      const { type_of_order, locations=[], location_partial_match = false } = params.data;
      return type_of_order === "T" 
        ? locations.some((data) => data.location_partial_match) 
        : location_partial_match;
    }
  };

  const finalPaginationOptions =
    props.customPaginationOptions && props.customPaginationOptions.length > 0
      ? props.customPaginationOptions
      : pageSizeOptions;


  return (
    <div style={ { height: '100%' } }>
      <Col span={ 24 } style={ { height: '100%' } }>
        <div className={`ag-theme-alpine ag-grid-container${props.source === "unassignedOrders" ? " preplan-unassigned-grid" : ""}`} data-testid="ag-grid-container" style={ { height: '100%', width: '100%', '--ag-font-size': '10px' } }>
          <AgGridReact
            headerHeight={ 40 }
            getRowStyle={handleGetRowStyle}
            floatingFiltersHeight={ 33 }
            rowClassRules={ rowClassRules }
            rowHeight={RowHeight ?? 40}
            loadingOverlayComponent={LoadingOverlay}
            // columnDefs={ visibleCols }
            columnDefs={visibleCols.filter(col => !uniqCols.includes(col.field))}
            rowData={ rowData }
            onSelectionChanged={ onSelectionChanged }
            {...(props.source === "unassignedOrders" && typeof props.onFilterChanged === "function"
              ? { onFilterChanged: props.onFilterChanged }
              : {})}
            onRowClicked={onRowClicked}
            onSortChanged={ saveSort }
            onGridReady={ (params) => {
              gridApiRef.current = params.api;
              onGridReady(params);
              applyFixedColumnWidths();
            } }
            suppressMoveWhenRowDragging
            rowDragEntireRow={enableRowDragEntireRow}
            rowSelection="multiple"
            defaultColDef={ defaultColDef }
            onColumnResized={ handleColumnResized }
            onColumnMoved={ handleColumnMoved }
            onColumnVisible={ handleColumnVisibilityChange }
            suppressDragLeaveHidesColumns
            isRowSelectable={ isRowSelectable }
            rowMultiSelectWithClick={ true }
            onRowDataUpdated={onRowDataUpdated}
            // fix scrolling issue by adding rowId so that it can identify the row uniquely
            getRowId={({data})=> {
              return data.id;
            }}
            suppressRowClickSelection
            tabToNextCell={tabToNextCell}
            reactiveCustomComponents={true}
            stopEditingWhenCellsLoseFocus={true}
            onCellEditingStopped={(params) => {
              handleCellData(params)
            }}
            enableCellTextSelection={ true }
            onFirstDataRendered={(params) => {
            }}
            onDragStarted={() => {
              clearTextSelection()
              document.body.classList.add("no-select")
            }}
            onDragStopped={() => {
              clearTextSelection()
              document.body.classList.remove("no-select")
            }}
            components={components}
            sideBar={showColumnToggle ? {
              toolPanels: [
                {
                  id: 'columns',
                  labelDefault: 'Columns',
                  labelKey: 'columns',
                  iconKey: 'columns',
                  toolPanel: 'agColumnsToolPanel',
                  toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                    suppressColumnFilter: false,
                  }
                }
              ],
              defaultToolPanel: ''
            } : undefined}
            // checkboxSelection={true}
          />
        </div>
        { showPagination && <div className="custom-pagination paddingRight20 paddingBottom5 paddingTop5">
            <GridPagination
              size="small"
              // pageSizeOptions={pageSizeOptions.map((option) =>
              //   option.value.toString()
              // )}
              pageSizeOptions={finalPaginationOptions.map((option) =>
                option.value.toString()
              )}
              total={pagination.totalRecords}
              showSizeChanger
              showQuickJumper={showQuickJumper ?? true}
              current={pagination.currentPage || 1}
              onChange={(page, pageSize) =>
                props.getPageWiseData(page, pageSize)
              }
              onShowSizeChange={(current, size) => props.onPerPageChanged(size)}
              showTotal={(total, range) => {
                return `${range[0]}-${range[1]} of ${total} items`;
              }}
              pageSize={pagination.pageSize}
              
            />
          </div>}
      </Col>
      <Drawer
        closable={false}
        onClose={() => closeModal()}
        visible={showRerraangeModal}
        width="310px"
        className ="orderDrawerScroll"
      >
        <Row type='flex' justify='space-between'>
          {/* <Col >
            <Button style={{ marginRight: '.5rem' }} size='small' type='primary' onClick={handleColAllVisibility}>Check All</Button>
          </Col>
          <Col>
            <Icon
              style={{ fontSize: '1rem' }}
              type="close"
              onClick={() => closeModal()}
            />
          </Col> */}
        </Row>
          <Row gutter={[10, 10]}>
            {renderColumns()}
          </Row>
      </Drawer>
    </div>
  );
};

const checkIsEqualProp = (prevProps, nextProps, keys) => {
  return _.every(keys, (key) => _.isEqual(prevProps[key], nextProps[key]));
}

export default React.memo(MyGridComponent, (prevProps, nextProps) => {
  if (prevProps.source === "unassignedOrders") {
    return checkIsEqualProp(prevProps, nextProps, [ 'rowData' , 'selectedIds', "showRerraangeModal","columnDefs" , "loading"]);
  } else if (prevProps.source === "billingInvoices") {
    // The billing listing loads up to 1200-4000 rows. Its rowData is memoized upstream
    // (OrderInvoicesGridList) off this.state.invoices, so the array reference only changes when
    // the data actually changes. Compare rowData by reference instead of _.isEqual, which would
    // otherwise deep-walk every row (~110 fields each) on every parent re-render of Invoices.
    // Same key set as the default branch otherwise.
    return prevProps.rowData === nextProps.rowData &&
      checkIsEqualProp(prevProps, nextProps, ['columnDefs', 'showRerraangeModal', 'pagination', "shouldResetRowSelectedKeys", "filterPlaceHolder"]);
  } else {
    return checkIsEqualProp(prevProps, nextProps, ['columnDefs', 'rowData', 'showRerraangeModal', 'pagination', "shouldResetRowSelectedKeys", "filterPlaceHolder"]);
  }
})


MyGridComponent.propTypes = {
  columnDefs: PropTypes.array.isRequired,
  rowData: PropTypes.array.isRequired,
  additionalColumnDefs: PropTypes.object,
  fetchData: PropTypes.func,
  saveSort: PropTypes.func,
  onRowSelection : PropTypes.func,
  onSortChanged : PropTypes.func,
  saveColumnWidth: PropTypes.func,
  onColumnMoved: PropTypes.func,
  showQuickFilter: PropTypes.bool,
  totalCols: PropTypes.array,
  showRerraangeModal: PropTypes.bool,
  enableRowClick: PropTypes.bool,
  uniqCols: PropTypes.array,
  source: PropTypes.string,
  customPaginationOptions: PropTypes.array,
  components: PropTypes.object,
  showColumnToggle: PropTypes.bool,
  enableRowDragEntireRow: PropTypes.bool,
};

MyGridComponent.defaultProps = {
  additionalColumnDefs: {},
  fetchData: () => { },
  saveSort: () => { },
  onSortChanged : () => {},
  saveColumnWidth: () => { },
  onColumnMoved: () => { },
  onRowSelection: () => { },
  showQuickFilter: false,
  totalCols: [],
  showRerraangeModal: false,
  enableRowClick: true,
  restoreRowSelection: () => {},
  shouldResetRowSelectedKeys: false,
  RowHeight: 22,
  uniqCols: [],
  source: "",
  customPaginationOptions: [],
  components: {},
  showColumnToggle: true,
  enableRowDragEntireRow: false,
};
